// common/features/team/player/toRosterCreatePayload.ts
import { initialPlayerValues, toUpdatePayload } from '../../profile/player'
import type { RosterCreatePayload } from '../../../services/team/rosterPlayer'
import type { RosterPlayerFormValues } from './types'

const trim = (v: string): string => (v ?? '').trim()

/**
 * Build the POST body for adding a player to a roster.
 *
 * Personal fields reuse the profile `toUpdatePayload` transform diffed against
 * the blank profile defaults — so the body carries every non-empty personal
 * field in the EXACT shape the profile PUT uses (names, birth_date, gender_id,
 * height in metres, weight, email/phone, address object, social, membership).
 * Names are then forced in explicitly (a create always needs them). gender_id
 * follows the shared transform: a real selection (id 1/2/3) → that int,
 * "Unspecified" → explicit null, an untouched dropdown → omitted. Roster keys are
 * appended verbatim from the legacy roster form. The injured/affiliate booleans
 * are always sent; customer_identifier only when present. Image is never
 * included (uploaded separately on save).
 */
export const toRosterCreatePayload = (
  values: RosterPlayerFormValues
): RosterCreatePayload => {
  // Personal fields, in the profile payload shape (diff vs blank = non-empty
  // only; gender_id handled by the shared transform — see above).
  const personal = toUpdatePayload(values, initialPlayerValues)

  const body: RosterCreatePayload = {
    ...personal,
    // A create needs names even though the diff already includes them when set.
    name_first: trim(values.firstName),
    name_last: trim(values.lastName),
    player_number: trim(values.playerNumber),
    player_type_id: parseInt(trim(values.positionId), 10),
    is_injured: Boolean(values.isInjured),
    is_affiliate: Boolean(values.isAffiliate),
    // Added players (create + copy) join the active/playing roster — the legacy
    // add/copy body always sent this. The edit (PUT) payload does NOT include it.
    is_playing: true,
  }

  const customerId = trim(values.customerIdentifier)
  if (customerId !== '') {
    body.customer_identifier = customerId
  }

  // Salesforce ID (Canlan only; the field is hidden elsewhere so this stays '').
  const salesforceId = trim(values.salesforceId)
  if (salesforceId !== '') {
    body.sf_id = salesforceId
  }

  return body
}
